import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";

import type { ChecklistItem, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { GlossaryPanel, useGlossary } from "../setup/glossary-drawer";
import { TimeCheckCard } from "../setup/time-check-card";
import { DenseButton, SetupCard, tone } from "../setup/ui";
import { OLD_TAB_MAP } from "../tournament-tabs";
import { Checklist } from "./checklist";
import { SetupSideSheet } from "./setup-side-sheet";
import type { SheetTarget } from "./setup-side-sheet";

const SHEET_TARGETS = new Set<SheetTarget>(["basics", "registration", "waiver", "officials", "promo"]);

/** Overview (F3): checklist, time check, where the old tabs went, glossary. */
export function OverviewTab({
  view,
  facilityId,
  onViewChange,
  onOpenEvents,
  onOpenSettings,
  onEditTime,
  initialSection,
}: {
  view: TournamentSetupView;
  facilityId: string;
  onViewChange: (next: TournamentSetupView) => void;
  onOpenEvents: () => void;
  onOpenSettings: () => void;
  onEditTime: () => void;
  initialSection?: string;
}) {
  const [sheet, setSheet] = useState<SheetTarget | null>(
    initialSection && SHEET_TARGETS.has(initialSection as SheetTarget) ? (initialSection as SheetTarget) : null,
  );
  // The side panel only fits from 1200 px; narrower, "Tournament terms" opens the drawer.
  const wide = useMediaQuery(useTheme().breakpoints.up("lg"), { noSsr: true });
  const [glossaryOpen, setGlossaryOpen] = useState(wide);
  useEffect(() => setGlossaryOpen(wide), [wide]);
  const glossary = useGlossary();
  const onAction = (item: ChecklistItem) => {
    if (item.target === "events") return onOpenEvents();
    if (SHEET_TARGETS.has(item.target as SheetTarget)) return setSheet(item.target as SheetTarget);
    return onOpenSettings();
  };
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
      <Box sx={{ flexGrow: 1, minWidth: { xs: "100%", lg: 0 } }}>
        <Checklist
          doneCount={view.required_done}
          items={view.checklist}
          laterLine={view.later_line}
          onAction={onAction}
          total={view.required_total}
        />
      </Box>
      <Box sx={{ width: { xs: "100%", lg: 330 }, flex: "none", display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TimeCheckCard compactUnits conflicts={view.court_conflicts} onEditAssumptions={onEditTime} result={view.time_check} />
        <SetupCard id="tabmap" title="Where the old tabs went">
          <Box sx={{ p: "4px 14px 10px" }}>
            {OLD_TAB_MAP.map((row, index) => (
              <Box key={row.old} sx={{ display: "flex", alignItems: "center", gap: 1, py: "5px", borderTop: index ? `1px solid ${tone.divider}` : 0, fontSize: 12.5 }}>
                <Box component="span" sx={{ color: tone.subtle, width: 120, flex: "none" }}>
                  {row.old}
                </Box>
                <span>{row.now}</span>
              </Box>
            ))}
          </Box>
        </SetupCard>
        {!glossaryOpen ? (
          <DenseButton
            onClick={() => (wide ? setGlossaryOpen(true) : glossary?.open())}
            size="sm"
            sx={{ alignSelf: "flex-start", minHeight: { xs: 44, md: 0 } }}
          >
            Tournament terms
          </DenseButton>
        ) : null}
      </Box>
      {glossaryOpen && wide ? (
        <Box sx={{ width: 380, flex: "none", position: "sticky", top: 16 }}>
          <GlossaryPanel onClose={() => setGlossaryOpen(false)} sx={{ maxHeight: "calc(100vh - 200px)" }} />
        </Box>
      ) : null}
      <SetupSideSheet facilityId={facilityId} onClose={() => setSheet(null)} onSaved={onViewChange} target={sheet} view={view} />
    </Box>
  );
}
